import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../contexts/UserContext';
import { useMessage } from '../contexts/MessageContext'; // Importar el hook de mensajes
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import GoogleLoginButton from './GoogleLogin';
import FacebookLoginButton from './FacebookLogin';

const Register = () => {
  const navigate = useNavigate();
  const { login } = useUser(); // Obtener la función de login del contexto
  const { showMessage } = useMessage(); // Obtener la función de mensajes del contexto
  const [isLeaving, setIsLeaving] = useState(false);
  const leaveTimeoutRef = useRef(null);
  const debounceTimersRef = useRef({ username: null, email: null });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [disponibilidad, setDisponibilidad] = useState({
    username: true,
    email: true,
    username_message: '',
    email_message: ''
  });

  const [verificando, setVerificando] = useState({
    username: false,
    email: false
  });

  const [disponibilidadComprobada, setDisponibilidadComprobada] = useState({
    username: false,
    email: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigateWithExit = (to) => {
    setIsLeaving(true);
    leaveTimeoutRef.current = window.setTimeout(() => {
      navigate(to);
    }, 220);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Verificar disponibilidad en tiempo real
    if (name === 'username' || name === 'email') {
      setDisponibilidadComprobada(prev => ({
        ...prev,
        [name]: false
      }));

      if (debounceTimersRef.current[name]) {
        window.clearTimeout(debounceTimersRef.current[name]);
      }

      debounceTimersRef.current[name] = window.setTimeout(() => {
        verificarDisponibilidad(name, value);
      }, 350);
    }
  };

  const verificarDisponibilidad = async (campo, valor) => {
    if (!valor.trim()) {
      setDisponibilidad(prev => ({
        ...prev,
        [campo]: true,
        [campo + '_message']: ''
      }));
      setDisponibilidadComprobada(prev => ({
        ...prev,
        [campo]: false
      }));
      return;
    }

    setVerificando(prev => ({
      ...prev,
      [campo]: true
    }));

    try {
      const response = await axios.post(`${API_BASE_URL}/verificar_disponibilidad.php`, {
        [campo]: valor
      });

      if (response.data.success) {
        const disp = response.data.disponibilidad;
        const disponible = Boolean(disp[campo]);

        setDisponibilidad(prev => ({
          ...prev,
          [campo]: disponible,
          [campo + '_message']: disp[campo + '_message'] || (disponible ? 'Disponible' : 'No disponible')
        }));
        setDisponibilidadComprobada(prev => ({
          ...prev,
          [campo]: true
        }));

        return disponible;
      }

      setDisponibilidad(prev => ({
        ...prev,
        [campo]: false,
        [campo + '_message']: 'No se pudo validar este campo'
      }));
      setDisponibilidadComprobada(prev => ({
        ...prev,
        [campo]: true
      }));
      return false;
    } catch (error) {
      console.error('Error verificando disponibilidad:', error);

      setDisponibilidad(prev => ({
        ...prev,
        [campo]: false,
        [campo + '_message']: 'No se pudo verificar disponibilidad. Intentalo de nuevo.'
      }));
      setDisponibilidadComprobada(prev => ({
        ...prev,
        [campo]: true
      }));
      return false;
    } finally {
      setVerificando(prev => ({
        ...prev,
        [campo]: false
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    const username = formData.username.trim();
    const email = formData.email.trim();

    if (!username || !email || !formData.password || !formData.confirmPassword) {
      showMessage('Todos los campos son obligatorios', 'error');
      return;
    }

    if (username.length < 3) {
      showMessage('El nombre de usuario debe tener al menos 3 caracteres', 'error');
      return;
    }

    if (formData.password.length < 6) {
      showMessage('La contrasena debe tener al menos 6 caracteres', 'error');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      showMessage('Las contraseñas no coinciden', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const [usernameDisponible, emailDisponible] = await Promise.all([
        verificarDisponibilidad('username', username),
        verificarDisponibilidad('email', email)
      ]);

      if (!usernameDisponible) {
        showMessage('El nombre de usuario ya esta en uso o no se pudo verificar', 'error');
        return;
      }

      if (!emailDisponible) {
        showMessage('El email ya esta en uso o no se pudo verificar', 'error');
        return;
      }

      console.log('Enviando solicitud de registro a:', `${API_BASE_URL}/crear_usuario.php`);
      console.log('Datos del formulario:', {
        username,
        email,
        password: formData.password
      });

      const response = await axios.post(`${API_BASE_URL}/crear_usuario.php`, {
        username,
        email,
        password: formData.password
      }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('Respuesta del servidor:', response);
      
      if (response.data.success) {
        showMessage('Usuario registrado correctamente.', 'success');
        // Iniciar sesión automáticamente con las credenciales recién registradas
        const loginResult = await login({
          username: formData.username,
          password: formData.password
        });

        if (loginResult.success) {
          // El contexto de usuario ya está actualizado
          navigateWithExit('/admin');
        } else {
          // Si el login automático falla, redirigir al login normal
          navigateWithExit('/login');
        }
      } else {
        showMessage(response.data.message || 'Error en el registro', 'error');
      }
    } catch (error) {
      console.error('Error en el registro:', error);
      if (error.response) {
        // El servidor respondió con un código de error distinto de 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
        showMessage(`Error en el registro: ${error.response.data.message || 'Error desconocido'}`, 'error');
      } else if (error.request) {
        // La solicitud se hizo pero no hubo respuesta
        console.error('Error request:', error.request);
        showMessage('Error en la conexión. Por favor, inténtalo de nuevo.', 'error');
      } else {
        // Algo sucedió al preparar la solicitud
        console.error('Error message:', error.message);
        showMessage(`Error en el registro: ${error.message}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = (user) => {
    console.log('Registro exitoso con Google:', user);
    // Aquí puedes manejar la lógica después del registro con Google
    // Por ejemplo, guardar el token en localStorage y redirigir al usuario
    navigateWithExit('/admin');
  };

  const handleGoogleError = (error) => {
    console.error('Error en el registro con Google:', error);
    showMessage('Error en el registro con Google: ' + error, 'error');
  };

  const handleFacebookSuccess = (user) => {
    console.log('Registro exitoso con Facebook:', user);
    // Aquí puedes manejar la lógica después del registro con Facebook
    // Por ejemplo, guardar el token en localStorage y redirigir al usuario
    navigateWithExit('/admin');
  };

  const handleFacebookError = (error) => {
    console.error('Error en el registro con Facebook:', error);
    showMessage('Error en el registro con Facebook: ' + error, 'error');
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');

    const toggleNoScroll = () => {
      if (mediaQuery.matches) {
        document.documentElement.classList.add('auth-no-scroll-mobile');
        document.body.classList.add('auth-no-scroll-mobile');
      } else {
        document.documentElement.classList.remove('auth-no-scroll-mobile');
        document.body.classList.remove('auth-no-scroll-mobile');
      }
    };

    toggleNoScroll();
    mediaQuery.addEventListener('change', toggleNoScroll);

    return () => {
      mediaQuery.removeEventListener('change', toggleNoScroll);
      document.documentElement.classList.remove('auth-no-scroll-mobile');
      document.body.classList.remove('auth-no-scroll-mobile');
    };
  }, []);

  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        window.clearTimeout(leaveTimeoutRef.current);
      }

      if (debounceTimersRef.current.username) {
        window.clearTimeout(debounceTimersRef.current.username);
      }

      if (debounceTimersRef.current.email) {
        window.clearTimeout(debounceTimersRef.current.email);
      }
    };
  }, []);

  const usernameValue = formData.username.trim();
  const emailValue = formData.email.trim();
  const passwordTooShort = Boolean(formData.password) && formData.password.length < 6;
  const usernameTooShort = Boolean(usernameValue) && usernameValue.length < 3;

  const canSubmit =
    Boolean(usernameValue) &&
    Boolean(emailValue) &&
    Boolean(formData.password) &&
    Boolean(formData.confirmPassword) &&
    !usernameTooShort &&
    !passwordTooShort &&
    formData.password === formData.confirmPassword &&
    disponibilidadComprobada.username &&
    disponibilidadComprobada.email &&
    disponibilidad.username &&
    disponibilidad.email &&
    !verificando.username &&
    !verificando.email &&
    !isSubmitting;

  const blockedReason = (() => {
    if (isSubmitting) return 'Registrando usuario...';
    if (!usernameValue) return 'Introduce un nombre de usuario';
    if (usernameTooShort) return 'El nombre de usuario debe tener al menos 3 caracteres';
    if (!emailValue) return 'Introduce un email';
    if (!formData.password) return 'Introduce una contrasena';
    if (passwordTooShort) return 'La contrasena debe tener al menos 6 caracteres';
    if (!formData.confirmPassword) return 'Confirma la contrasena';
    if (formData.password !== formData.confirmPassword) return 'Las contrasenas no coinciden';
    if (verificando.username || verificando.email) return 'Espera a que termine la verificacion';
    if (!disponibilidadComprobada.username || !disponibilidadComprobada.email) return 'Debes validar disponibilidad de usuario y email';
    if (!disponibilidad.username) return disponibilidad.username_message || 'El nombre de usuario ya esta en uso';
    if (!disponibilidad.email) return disponibilidad.email_message || 'El email ya esta en uso';
    return '';
  })();

  return (
    <motion.section
      initial={{ opacity: 0, y: 20, scale: 0.985 }}
      animate={isLeaving ? { opacity: 0, y: -16, scale: 0.985 } : { opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex h-[calc(100dvh-6rem)] items-center overflow-hidden px-3 py-3 sm:h-auto sm:min-h-[calc(100vh-4rem)] sm:px-6 sm:py-10"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-24 top-0 h-72 w-72 rounded-full bg-cyan-300/35 blur-3xl dark:bg-cyan-900/30" />
        <div className="absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-blue-300/35 blur-3xl dark:bg-blue-900/30" />
      </div>

      <div className="relative mx-auto w-full max-w-xl max-h-full">
        <div className="rounded-3xl border border-slate-200/70 bg-white/85 p-4 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/85 sm:p-7 lg:p-8">
          <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 sm:text-3xl">Crear cuenta</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 sm:mt-2">Completa tus datos para unirte al concurso.</p>

          <form onSubmit={handleSubmit} className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
            <div>
              <label htmlFor="register-username" className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200 sm:mb-2">Usuario</label>
              <input
                id="register-username"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className={`w-full rounded-xl border bg-white px-4 py-2.5 text-slate-900 shadow-sm outline-none transition-all duration-200 focus:ring-4 dark:bg-slate-800 dark:text-slate-100 sm:py-3 ${
                  !disponibilidad.username
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:border-red-500 dark:focus:ring-red-900/30'
                    : disponibilidad.username && formData.username
                      ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-100 dark:border-emerald-500 dark:focus:ring-emerald-900/30'
                      : 'border-slate-300 focus:border-blue-500 focus:ring-blue-200 dark:border-slate-600 dark:focus:border-blue-400 dark:focus:ring-blue-900/40'
                }`}
                placeholder="Tu nombre de usuario"
                autoComplete="username"
              />
              {verificando.username && (
                <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">Verificando disponibilidad...</p>
              )}
              {!disponibilidad.username && formData.username && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{disponibilidad.username_message}</p>
              )}
              {disponibilidad.username && disponibilidadComprobada.username && formData.username && !verificando.username && (
                <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Nombre de usuario disponible</p>
              )}
            </div>

            <div>
              <label htmlFor="register-email" className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200 sm:mb-2">Email</label>
              <input
                id="register-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`w-full rounded-xl border bg-white px-4 py-2.5 text-slate-900 shadow-sm outline-none transition-all duration-200 focus:ring-4 dark:bg-slate-800 dark:text-slate-100 sm:py-3 ${
                  !disponibilidad.email
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-100 dark:border-red-500 dark:focus:ring-red-900/30'
                    : disponibilidad.email && formData.email
                      ? 'border-emerald-400 focus:border-emerald-500 focus:ring-emerald-100 dark:border-emerald-500 dark:focus:ring-emerald-900/30'
                      : 'border-slate-300 focus:border-blue-500 focus:ring-blue-200 dark:border-slate-600 dark:focus:border-blue-400 dark:focus:ring-blue-900/40'
                }`}
                placeholder="nombre@email.com"
                autoComplete="email"
              />
              {verificando.email && (
                <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">Verificando disponibilidad...</p>
              )}
              {!disponibilidad.email && formData.email && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{disponibilidad.email_message}</p>
              )}
              {disponibilidad.email && disponibilidadComprobada.email && formData.email && !verificando.email && (
                <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Email disponible</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
              <div>
                <label htmlFor="register-password" className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200 sm:mb-2">Contraseña</label>
                <input
                  id="register-password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-slate-900 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-900/40 sm:py-3"
                  placeholder="Minimo 8"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label htmlFor="register-confirmPassword" className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200 sm:mb-2">Confirmar</label>
                <input
                  id="register-confirmPassword"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-slate-900 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-900/40 sm:py-3"
                  placeholder="Repite"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-blue-300/40 transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-blue-700 dark:hover:bg-blue-600 sm:py-3 sm:text-base"
            >
              {isSubmitting ? 'Registrando...' : 'Registrarse'}
            </button>

            {!canSubmit && blockedReason && (
              <p className="text-center text-xs text-amber-700 dark:text-amber-300">{blockedReason}</p>
            )}
          </form>

          <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-300 sm:mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link
              to="/login"
              onClick={(e) => {
                e.preventDefault();
                navigateWithExit('/login');
              }}
              className="font-bold text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Inicia sesión aquí
            </Link>
          </p>
        </div>
      </div>
    </motion.section>
  );
};

export default Register;
